import { inject } from '../../../../../../common/container/inject';
import { injectPrimitive } from '../../../../../../common/store/base/injectPrimitive';
import { saveFileInBrowser } from '../../../../../../common/utils/saveFileInBrowser';
import { injectRootService } from '../../../../../../service/RootServiceFactory';
import { UserSystemRoleModelPermissionMap } from '../../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { IMainLayoutDomainStore } from '../../../../../../view/layout/main/store/domain/IMainLayoutDomainStore';
import { ILocalization, ILocalizationToken } from '../../../../application/language/ILocalization';
import { ILearningRootService, LearningRootServiceToken } from '../../../../service/LearningRootService';

export class LearningReportDomain {
  constructor(
    public layoutDomain: IMainLayoutDomainStore,
    public anchorEl = injectPrimitive<HTMLButtonElement | null>(null),
    public isCanDownload = injectPrimitive<boolean>(false),
    public i18n: ILocalization = inject<ILocalization>(ILocalizationToken),
    private learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
    private rootService = injectRootService(layoutDomain.serviceType.value),
  ) {}

  boot = async () => {
    const isCanDownload = this.layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]);
    this.isCanDownload.setValue(isCanDownload);
  };

  handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.anchorEl.setValue(event.currentTarget);
  };
  handleClose = () => {
    this.anchorEl.setValue(null);
  };

  private collectExcludedRoleIds(roles: any[], excludedPermissionKeys: string[] = []): Set<string> {
    const baseKey = UserSystemRoleModelPermissionMap['global-allow-any'] ?? 'global-allow-any';
    const keys = new Set([baseKey, ...excludedPermissionKeys]);

    return new Set(
      (roles || [])
        .filter((r) => Array.isArray(r?.permissions?.allowedPermissions))
        .filter((r) => {
          const perms: string[] = r.permissions.allowedPermissions as any;
          return perms.some((p) => keys.has(p));
        })
        .map((r) => r.id),
    );
  }

  private getUserRoleIds(u: any): string[] {
    const result = new Set<string>();

    if (u?.systemRoleId) result.add(u.systemRoleId);

    if (Array.isArray(u?.systemRoleIds)) {
      for (const id of u.systemRoleIds) if (id) result.add(id);
    }

    if (Array.isArray(u?.roles)) {
      for (const r of u.roles) {
        if (typeof r === 'string') result.add(r);
        else if (r?.id) result.add(r.id);
      }
    }

    if (typeof u?.roleId === 'string') result.add(u.roleId);
    if (Array.isArray(u?.roleIds)) {
      for (const id of u.roleIds) if (id) result.add(id);
    }

    return Array.from(result);
  }

  private excludeAdmins(users: any[], excludedRoleIds: Set<string>) {
    return (users || []).filter((u) => {
      const roleIds = this.getUserRoleIds(u);
      if (roleIds.length === 0) return true;
      return !roleIds.some((id) => excludedRoleIds.has(id));
    });
  }

  genereteDocx = async (unitSummuryTemplatePath?: string) => {
    const [usersRes, rolesRes] = await Promise.all([
      this.rootService.user.entity.search({
        limit: 1000000,
        fields: ['id', 'displayName', 'systemRoleId', 'roles', 'systemRoleIds', 'roleId', 'roleIds'],
      }),
      this.rootService.systemRole.entity.search({
        limit: 1000000,
        fields: ['id', 'name', 'permissions'],
      }),
    ]);

    const userList = usersRes.data;
    const systemRoleList = rolesRes.data;

    const excludedRoleIds = this.collectExcludedRoleIds(systemRoleList);
    const usersWithoutAdmins = this.excludeAdmins(userList, excludedRoleIds);

    const projects = (
      await this.rootService.project.entity.search({
        limit: 100000,
        fields: ['id', 'name', 'rolesMap'],
      })
    ).data;

    const blobDocx = await this.learningRootService.report.generateProjectUnitSummuryByUsers(
      usersWithoutAdmins,
      systemRoleList,
      projects,
      unitSummuryTemplatePath,
    );

    this.saveFile(this.i18n.translate('learningAdminPage.trainingReportDOCXFileLabel'), blobDocx);
  };

  generatePdf = async (unitSummuryTemplatePath?: string) => {
    const [usersRes, rolesRes] = await Promise.all([
      this.rootService.user.entity.search({
        limit: 1000000,
        fields: ['id', 'displayName', 'systemRoleId', 'roles', 'systemRoleIds', 'roleId', 'roleIds'],
      }),
      this.rootService.systemRole.entity.search({
        limit: 1000000,
        fields: ['id', 'name', 'permissions'],
      }),
    ]);

    const userList = usersRes.data;
    const systemRoleList = rolesRes.data;

    const excludedRoleIds = this.collectExcludedRoleIds(systemRoleList);
    const usersWithoutAdmins = this.excludeAdmins(userList, excludedRoleIds);

    const projects = (
      await this.rootService.project.entity.search({
        limit: 100000,
        fields: ['id', 'name', 'rolesMap'],
      })
    ).data;

    const blobPdf = await this.learningRootService.report.generateProjectUnitSummuryByUsersPdf(
      usersWithoutAdmins,
      systemRoleList,
      projects,
      unitSummuryTemplatePath,
    );

    this.saveFile(this.i18n.translate('learningAdminPage.trainingReportPDFFileLabel'), blobPdf);
  };

  saveFile(filename: string, blob: any) {
    saveFileInBrowser(filename, blob);
  }

  handleReportDocx = (unitSummuryTemplatePath?: string) => {
    this.genereteDocx(unitSummuryTemplatePath);
    this.handleClose();
  };

  handleReportPdf = (unitSummuryTemplatePath?: string) => {
    this.generatePdf(unitSummuryTemplatePath);
    this.handleClose();
  };
}
